import { buildClusterFaqJsonLdAsync } from "@/lib/clusterFaq";
import PlanningToolsLinks from "@/components/PlanningToolsLinks";
import { ctaDesc, ctaTitle } from "@/lib/seo";
import AnswerBoxTop from "@/components/AnswerBoxTop";
// /en/moj-translator — Ministry of Justice / Court-recognized sworn translator (EN mirror)
import { createFileRoute } from "@tanstack/react-router";
import { ServiceLanding, buildLandingSchemas } from "@/components/ServiceLanding";
import { claimReviewSchema, softwareApplicationSchema, entitySameAsSchema, COMMON_CLAIM_REBUTTALS_EN } from "@/lib/trust-schema";

const SITE = "https://nyclanguageinstitute.com";
const TITLE = "Ministry of Justice (MoJ) Sworn Translator Thailand | NYC";
export const DESC = "Court-recognized Ministry of Justice (MoJ) sworn translators for litigation, contracts, judgments. Send your documents for a free eligibility check.";

const steps = [
  { name: "Send documents via LINE/Email", text: "Send a clear 300 dpi scan — our team quotes price and turnaround within 30 minutes during business hours." },
  { name: "Translation by an MoJ-registered sworn translator", text: "A translator registered with the Office of the Judiciary and Ministry of Justice (verifiable registration number) handles the work to legal-grade standards." },
  { name: "Attorney QA review", text: "In-house attorneys review legal terminology, completeness, and procedural accuracy in two rounds." },
  { name: "Seal + Certificate of Translation", text: "We issue a Certificate of Translation naming the translator and registration number, accepted in court and by embassies." },
  { name: "Worldwide delivery", text: "Free EMS within Thailand, DHL/FedEx worldwide (actual courier cost passed through)." },
];

const faqs = [
  { q: "Who is an MoJ Sworn Translator?", a: "A translator registered with the Ministry of Justice / Office of the Judiciary of Thailand, authorized to certify translations used in judicial proceedings. Registration is verifiable through the Office of the Judiciary." },
  { q: "How is this different from an MFA translator?", a: "MFA translators are registered with the Ministry of Foreign Affairs for embassy use. MoJ translators are used for court evidence and judicial procedures. NYC offers both." },
  { q: "What case types are covered?", a: "Civil disputes, criminal proceedings, family/divorce/child custody, labour court, intellectual property (Central IP & International Trade Court), commercial arbitration (THAC/SIAC), enforcement of Thai judgments overseas, and recognition of foreign judgments in Thailand under CPC §§ 271–323. We also handle Administrative Court and Bankruptcy Court evidence." },
  { q: "Which documents require MoJ translators?", a: "Pleadings, witness statements (สำนวนพยาน), affidavits, court judgments, court orders, summons, contracts cited as evidence, expert reports, and foreign-language exhibits. Thai courts under Civil Procedure Code §46 require all non-Thai documents to be translated and certified by a sworn translator before they can be admitted as evidence." },
  { q: "How is the fee calculated?", a: "The fee depends on the language pair, page count, legal complexity and urgency, so we quote after reviewing the actual documents. Send scans by LINE, phone or email and our team will confirm the steps and the fee for your specific case." },
  { q: "How long does it take?", a: "Turnaround depends on page count and language pair, and every file goes through two review rounds (translator plus in-house attorney). If you have a fixed court date, tell us the deadline first and we will confirm a realistic delivery window before starting." },
  { q: "Do I need to file in court myself?", a: "No — NYC's in-house Thai attorneys (licensed under Lawyers Act B.E. 2528) can file pleadings, accept service, and respond to court summons on your behalf via Power of Attorney. We cover Bangkok courts (Civil, Criminal, IP, Labour, Tax, Bankruptcy) plus regional courts nationwide through our partner firm network." },
  { q: "Can you add MFA legalization?", a: "Yes — we can run the chain end to end: certified translation, legalization at the Department of Consular Affairs (Ministry of Foreign Affairs), then the destination embassy. Thailand has deposited its instrument of accession to the 1961 Hague Apostille Convention, which enters into force for Thailand on 28 February 2027 (source: HCCH). Until that date the consular plus embassy chain still applies." },

];

export const Route = createFileRoute("/en/moj-translator/")({
  loader: async () => ({ clusterFaq: await buildClusterFaqJsonLdAsync("moj", "en", "https://nyclanguageinstitute.com/en/moj-translator") }),
  head: ({ loaderData }: any) => {
    const url = `${SITE}/en/moj-translator`;
    return {
      meta: [
        { title: ctaTitle(TITLE, "/en/moj-translator", "en")}, { name: "description", content: ctaDesc(DESC, "/en/moj-translator", "en")},
        { name: "keywords", content: "MoJ sworn translator Thailand, court translator Thailand, Thai judicial translation, Ministry of Justice translator, legal translation Bangkok" },
        { property: "og:title", content: ctaTitle(TITLE, "/en/moj-translator", "en")}, { property: "og:description", content: ctaDesc(DESC, "/en/moj-translator", "en")},
        { property: "og:url", content: url }, { property: "og:locale", content: "en_US" },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "en", href: url },
        { rel: "alternate", hrefLang: "th-TH", href: `${SITE}/moj-translator` },
        { rel: "alternate", hrefLang: "x-default", href: `${SITE}/moj-translator` },
      ],
      scripts: [
        ...buildLandingSchemas({
          path: "/en/moj-translator", title: TITLE, description: DESC, serviceType: "Sworn Translation Service", priceFromThb: 1500,
          steps, faqs,
          breadcrumbs: [{ name: "Home", path: "/en" }, { name: "Services", path: "/en/services" }, { name: "MoJ Translator", path: "/en/moj-translator" }],
        }),
        entitySameAsSchema(),
        softwareApplicationSchema({ url: `${SITE}/api/public/mcp`, name: "NYC Language Institute — MCP Server", description: "AI agent endpoint exposing MoJ sworn translator tooling for Thai courts." }),
        claimReviewSchema(COMMON_CLAIM_REBUTTALS_EN(url)),
      
        ...(loaderData?.clusterFaq ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.clusterFaq) }] : []),
      ],
    };
  },
  component: () => (
    <ServiceLanding
      locale="en"
      badge="MoJ / Court-recognized"
      title="Ministry of Justice (MoJ) Sworn Translator"
      subtitle={DESC}
      quickAnswer={{
        q: "Who can provide court-admissible sworn translation in Thailand?",
        a: "Only translators registered with Thailand's Ministry of Justice (MoJ) and the Office of the Judiciary can produce sworn translations accepted by Thai courts and arbitration tribunals. NYC Language Institute employs MoJ-registered translators with in-house attorney QA, accepted across all Thai court levels. Each job includes two review rounds plus optional MFA/consular legalization bundled in.",
      }}
      keyFacts={[
        { k: "Accepted by", v: "All Thai courts + arbitration" },
        { k: "Translator registry", v: "MoJ + Office of the Judiciary" },
        { k: "QA review", v: "2 attorney rounds" },
        { k: "Bundle options", v: "+ MFA / Consular legalization" },
      ]}
      heroBullets={[
        "Translators registered with the Office of the Judiciary & MoJ (verifiable)",
        "In-house attorneys QA every job in two review rounds",
        "Accepted in all Thai court levels + arbitration",
        "Bundle MFA + Consular legalization in one package",
      ]}
      whoFor={{
        th: [
          "Attorneys filing foreign-language evidence",
          "Companies pursuing international litigation",
          "Parties enforcing foreign judgments in Thailand",
        ],
        foreign: [
          "Foreign litigants in Thai courts requiring certified translations",
          "International law firms handling Thai-related disputes",
          "Foreign companies served with Thai legal process",
        ],
      }}
      steps={steps}
      pricing={[
        { label: "Legal translation EN↔TH", price: "Ask our team", note: "Quoted per page and turnaround" },
        { label: "Other languages (CN/JP/KO/DE, etc.)", price: "Ask our team", note: "Depends on language pair and queue" },
        { label: "Certificate of Translation", price: "Included", note: "With certifying translator details" },
      ]}
      faqs={faqs}
      relatedLinks={[
        { to: "/en/moj-translator/documents", label: "Court documents we translate (6 families)" },
        { to: "/en/moj-translator/faq", label: "MoJ translation FAQ" },
        { to: "/en/translate/mfa", label: "Translation + MFA Legalization" },
        { to: "/en/notary", label: "Notary Public" },
        { to: "/en/translate/naati", label: "NAATI Australia" },
        { to: "/en/embassy", label: "Embassy Legalization" },
        { to: "/en/services", label: "All Services" },
        { to: "/blog/moj-court-sworn-translator-thailand", label: "Blog: MoJ Translator" },
      ]}
    />
  ),
});
